import type { Dayjs } from 'dayjs'
import { z } from 'zod'
import type { AccountPageFoodie } from '~/types/foodies'
import type { AccountPageCook } from '~/types/cooks'
import type { Role } from '~/types/auth'
import type { PromotionsEnum } from '~/types/registration'
import type { WebsiteSurvey } from '~/types/surveys'
import type { NullableBoolean } from '~/types/utils'
import { getErrorMessage } from '~/utils/zodUtils'
import type { UserWallet } from '~/types/userWallets'
import {
  type Gender,
  GenderEnum,
  type Locale,
  type RoleId,
  type UserId,
  type UserProfileId,
  UserTypeEnum
} from '#shared/types/users'
import type { CookId } from '#shared/types/cooks.ts'
import type { FoodieId } from '#shared/types/foodies.ts'

export const GenderOptions = z
  .enum(GenderEnum, getErrorMessage('pick-a-gender'))
  .or(z.string().trim().min(1, getErrorMessage('pick-a-gender')))

export interface UpdatedUser {
  email: string
  userProfile: UserProfile

  acceptsOtherLanguages: NullableBoolean

  // Cook/ foodie
  mailUpdatesAccepted: boolean
  bankaccountNumber: string | null
  bankaccountName: string | null
}

export interface UpdatedUserWithId extends UpdatedUser {
  id: UserId
}

export interface User {
  id: UserId
  name: string
  email: string
  emailVerifiedAt: Dayjs | null
  locale: Locale
  balanceThreshold: number
  isPrepaid: boolean
  userProfile: UserProfileWithId
  surveyOnHold: boolean
  role: UserTypeEnum
  roles: Role[]
  balance: number
  hasActiveMatch: boolean
}

export interface UserProfile {
  firstname: string
  lastname: string
  phone: string
  phoneAlt: string | null
  gender: Gender
  birthdate: Dayjs | null
  housenumber: string
  postalcode: string
  street: string
  city: string
  municipality: string
  neighborhood: string
  district: string
  borough: string | null
  addressComments: string | null
  promotions?: PromotionsEnum[]
}

export interface UserProfileWithId extends UserProfile {
  id: UserProfileId
  userId: UserId
}

export interface NewContactDetails {
  phone: string
  phoneAlt: string | null
  housenumber: string
  postalcode: string
  street: string
  city: string
  municipality: string
  neighborhood: string
  district: string
  borough: string | null
  addressComments: string | null
}

interface RelatedUser {
  fullName: string
  roleId: RoleId
  userRole: UserTypeEnum
  userId: UserId
}

export interface DetailUser extends User {
  roleId: CookId | FoodieId | null

  hasPaymentInformation: boolean
  hasRecurringDonations: boolean
  hasLinkedThinkificAccount: boolean
  hasCompletedThinkificSignUp: boolean
  isArchived: boolean

  donationBoxesCount: number

  foodie: AccountPageFoodie | null
  cook: AccountPageCook | null
  surveys: WebsiteSurvey[]
  relatedUsers: RelatedUser[]
}

export interface PDFResponse {
  error: boolean
  status: 'ok' | undefined
}

export interface UserWalletPaginationResponse {
  data: UserWallet[]
  links: {
    next: string | null
  }
  meta: {
    currentPage: number
    perPage: number
    total: number
  }
}

export type ThinkificSignupResponse =
  | { signInUrl: string }
  | { userExists: boolean; linked: boolean }

export type CookieDecision = 'allow' | 'deny' | 'unknown'
export type CookieCategory = 'ads' | 'marketing'
